import { Multicall } from "ethereum-bsc-multicall";
import { BigNumber, ethers } from "ethers";
import { useDispatch, useSelector } from "react-redux";

class Query {
  constructor() {
    this.dispatch = useDispatch();
    this.walletStore = useSelector((state) => state.wallet);
    this.provider = null;
  }

  async getProvider() {
    if (this.provider) {
      return this.provider;
    }
    await window.ethereum.enable();
    const provider = new ethers.providers.Web3Provider(window.ethereum);
    this.provider = provider;
    return provider;
  }

  isConnected() {
    return !!this.getAddress();
  }

  getReservesQuery(token) {
    return {
      reference: `reserves_${token.contractAddress}`,
      contractAddress: token.pairAddress,
      abi: [
        {
          name: "getReserves",
          type: "function",
          stateMutability: "view",
          inputs: [],
          outputs: [
            { name: "_reserve0", type: "uint112" },
            { name: "_reserve1", type: "uint112" },
          ],
        },
      ],
      calls: [{ reference: `reserves_${token.contractAddress}`, methodName: "getReserves", methodParameters: [] }],
    };
  }

  getPairQuery(pcsAddr, bnbAddr, token) {
    return {
      reference: `pair_${token.contractAddress}`,
      contractAddress: pcsAddr,
      abi: [
        {
          name: "getPair",
          type: "function",
          stateMutability: "view",
          inputs: [
            { name: "", type: "address" },
            { name: "", type: "address" },
          ],
          outputs: [{ name: "", type: "address" }],
        },
      ],
      calls: [{ reference: `pair_${token.contractAddress}_fn`, methodName: "getPair", methodParameters: [token.contractAddress, bnbAddr] }],
    };
  }

  pairFromQueryResult(referenceKey, queryResult) {
    const contractAddress = referenceKey.split("_")[1];
    return {
      [contractAddress]: queryResult.callsReturnContext[0].returnValues[0],
    };
  }

  async getPrice(tokens) {
    const bnbAddr = "0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c";
    const bnbDecimals = 10 ** 18;
    const pcsV2RouterAddr = "0x10ED43C718714eb63d5aA57B78B54704E256024E";
    const pcsV2FactoryAddr = "0xcA143Ce32Fe78f1f7019d7d551a6402fC5350c73";

    if (!tokens) {
      return {};
    }

    const provider = await this.getProvider();

    const multicall = new Multicall({ ethersProvider: provider });

    const contractCallContext = tokens.map((t) => this.getPairQuery(pcsV2FactoryAddr, bnbAddr, t));
    const response = await multicall.call(contractCallContext);

    // console.log("liquidity call", response);

    const liquidityPairDict = Object.assign({}, ...Object.keys(response.results).map((k) => this.pairFromQueryResult(k, response.results[k])));

    for (let i = 0; i < tokens.length; i++) {
      tokens[i].pairAddress = liquidityPairDict[tokens[i].contractAddress];
    }

    // NOT WORKING - only first result being returned, revisit
    // contractCallContext = tokens.map((t) => this.getReservesQuery(t));
    // response = await multicall.call(contractCallContext);
    const ABI = ["function getReserves() view returns(uint112, uint112)", "function token0() view returns(address)"];
    for (let i = 0; i < tokens.length; i++) {
      if (tokens[i].pairAddress.startsWith("0x000000000")) {
        continue;
      }
      try {
        const readOnlyContract = new ethers.Contract(tokens[i].pairAddress, ABI, provider);
        const token0Addr = await readOnlyContract.token0().catch((e) => console.log(e));
        const response = await readOnlyContract.getReserves().catch((e) => console.log(e));
        if (!response) {
          continue;
        }
        let [reserve0, reserve1] = response;
        if (token0Addr === bnbAddr) {
          reserve0 = reserve0
            .div(10 ** 9)
            .div(10 ** 9)
            .toNumber();
          reserve1 = reserve1.div(10 ** parseInt(tokens[i].tokenDecimal)).toNumber();
          tokens[i].priceBnb = reserve0 / parseFloat(reserve1);
        } else {
          // console.log(tokens[i].tokenName, reserve0, reserve1, tokens[i].tokenDecimal);
          reserve1 = reserve1
            .div(10 ** 9)
            .div(10 ** 9)
            .toNumber();

          reserve0 = reserve0.div(10 ** parseInt(tokens[i].tokenDecimal)).toNumber();
          tokens[i].priceBnb = reserve1 / parseFloat(reserve0);
        }
      } catch (e) {
        console.log(e);
      }
    }

    return Object.assign(
      {},
      ...tokens.map((t) => {
        return { [t.contractAddress]: t };
      })
    );
  }

  async setBalances(addr, skipCache) {
    const address = addr || this.getAddress();
    // TODO : timed cache
    const balancesKey = `balances_${address}`;
    if (!skipCache && balancesKey in localStorage && localStorage[balancesKey]) {
      const balances = JSON.parse(localStorage[balancesKey]);
      if (balances && balances.length > 0) {
        return;
      }
    }
    const balances = await this.getBalances(address);
    if (balances) {
      await this.dispatch(handleTokens(balances, address));
    }
  }

  balanceFromQueryResult(referenceKey, queryResult) {
    const contractAddress = referenceKey.split("_")[1];
    const decimals = parseInt(queryResult.callsReturnContext[0].reference.split("_")[1]);
    let amount = 0;
    try {
      amount = BigNumber.from(queryResult.callsReturnContext[0].returnValues[0]).div(BigNumber.from(10).pow(decimals)).toNumber();
    } catch (e) {
      console.log(e);
    }
    return {
      [contractAddress]: amount,
    };
  }

  getBalanceQuery(addr, token) {
    return {
      reference: `balance_${token.contractAddress}_${addr}`,
      contractAddress: token.contractAddress,
      abi: [{ name: "balanceOf", type: "function", stateMutability: "view", inputs: [{ name: "account", type: "address" }], outputs: [{ name: "", type: "uint256" }] }],
      calls: [{ reference: `decimals_${token.tokenDecimal}`, methodName: "balanceOf", methodParameters: [addr] }],
    };
  }

  async getBalances(addr, tokens) {
    if (!addr || !tokens) {
      return {};
    }
    try {
      const provider = await this.getProvider();

      const multicall = new Multicall({ ethersProvider: provider });

      const contractCallContext = tokens.map((t) => this.getBalanceQuery(addr, t));

      const response = await multicall.call(contractCallContext);

      const asDict = Object.assign({}, ...Object.keys(response.results).map((k) => this.balanceFromQueryResult(k, response.results[k])));
      return asDict;
    } catch (e) {
      console.log(e);
      return {};
    }
  }
}

export default Query;
